import { useState } from 'react';
import { ticketsAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';

//: La frase, igual que en el backend (`services/borrado_operativo.CONFIRMACION`). **Dice qué se
//: borra** y no "CONFIRMAR": una frase genérica se tipea sin leerla.
const FRASE = 'BORRAR TODO LO OPERATIVO';

/**
 * Un gesto en vez de cinco, para empezar una ronda de pruebas desde cero.
 *
 * **Por qué existe.** Hoy hay que apretar *Vaciar tickets* y *Huérfanos* en Casos, *Vaciar
 * conversaciones* en Monitor y *Eliminar consumos* en Tablero —cuatro gestos en tres pantallas, cada
 * uno con su frase— y acordarse de que queda una quinta cosa que **ningún botón limpia**: la memoria
 * de los casos resueltos. Cuatro gestos repartidos no son una ceremonia de seguridad: son una forma
 * de olvidarse uno, y el que se olvida es siempre el quinto.
 *
 * **No borra nada nuevo.** El backend llama a las mismas funciones que esos botones ya llaman.
 *
 * **Es del tenant, no de una cápsula**, y se dice: las cuatro operaciones que compone son por
 * tenant. Dibujarlo como si fuera de la cápsula abierta sería prometer un alcance que no tiene — el
 * defecto que este producto viene arreglando desde el lunes.
 */
export default function BorradoOperativoTotal({ tenantId, onSuccess }) {
  const [abierto, setAbierto] = useState(false);
  const [frase, setFrase] = useState('');
  const [corriendo, setCorriendo] = useState(false);
  const [resultado, setResultado] = useState(null);

  const correr = async () => {
    setCorriendo(true);
    try {
      const res = await ticketsAPI.borradoOperativoTotal(tenantId, { confirmation: FRASE });
      setResultado(res.data);
      const fallidos = res.data?.fallidos || [];
      if (fallidos.length) {
        // **Un paso que falla no se tapa con un toast verde.** Se sigue a propósito —dejar la base
        // a medio limpiar y abortar es peor— pero hay que poder ver cuál quedó.
        toast.error(`Terminó con fallas en: ${fallidos.join(', ')}. Mirá el detalle abajo.`);
      } else {
        toast.success('Borrado operativo completo');
      }
      setAbierto(false);
      setFrase('');
      await onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudo completar el borrado');
      throw e;
    } finally {
      setCorriendo(false);
    }
  };

  return (
    <Card className="rounded-sm border-red-200" data-testid="borrado-operativo-total">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-red-700 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4" /> Borrado operativo total
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-zinc-600 leading-relaxed">
          Deja el <b>tenant entero</b> como recién instalado para empezar una ronda de pruebas. Hace,
          de una, lo mismo que apretar los cuatro botones que ya existen repartidos en tres
          pantallas, <b>más la memoria de los casos</b>, que no tiene botón en ningún lado.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
          <div className="border border-red-200 bg-red-50/40 rounded-sm p-2">
            <p className="font-semibold text-red-800 mb-1">Se borra</p>
            <ul className="space-y-0.5 text-zinc-700">
              <li>· los casos, con todo lo que cuelga: tareas, horas, workspace, artefactos, avisos, dependencias</li>
              <li>· los documentos huérfanos que quedaron apuntando a nada</li>
              <li>· las conversaciones y el detalle de cada turno</li>
              <li>· el historial de consumo del modelo</li>
              <li>· lo aprendido de los casos resueltos, <b>y sus vectores</b></li>
              <li>· el log del sistema (Registros) y el rastro de los turnos</li>
            </ul>
          </div>
          <div className="border border-zinc-200 bg-zinc-50 rounded-sm p-2">
            <p className="font-semibold text-zinc-700 mb-1">Queda intacto</p>
            <ul className="space-y-0.5 text-zinc-600">
              <li>· personas, roles y equipos</li>
              <li>· la configuración de cápsula: vocabulario, intents, plantillas, estados, guías</li>
              <li>· agentes, soul y rules</li>
              <li>· canales, conexiones y habilidades</li>
              <li>· la base de conocimiento y el grafo del ERP</li>
              <li>· lo que el área sabe de cada persona</li>
            </ul>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="rounded-sm text-xs border-red-300 text-red-700 hover:bg-red-50"
          onClick={() => setAbierto(true)}
          data-testid="abrir-borrado-operativo"
        >
          Borrado operativo total
        </Button>

        {/* El detalle **por paso** y no un total: lo que alguien necesita saber después de apretar
            esto es si la memoria se fue y si los vectores se fueron con ella, y eso son dos números
            distintos. */}
        {resultado && (
          <div className="border-t border-zinc-100 pt-2 space-y-1" data-testid="resultado-borrado">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Lo que pasó</p>
            {(resultado.pasos || []).map((p) => (
              <div key={p.paso} className="text-[11px]">
                <span className={p.ok ? 'text-emerald-700' : 'text-red-700'}>
                  {p.ok ? '✓' : '⚠'} {p.paso}
                </span>
                <span className="text-zinc-500"> — {p.que}</span>
                <div className="text-[10px] text-zinc-500 font-mono break-all pl-4">
                  {p.ok ? JSON.stringify(p.resultado) : p.error}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <ConfirmActionDialog
        open={abierto}
        onOpenChange={(a) => { if (!a) { setAbierto(false); setFrase(''); } }}
        title="Borrado operativo total"
        description={
          'Borra los casos, las conversaciones, los consumos y la memoria de los casos del tenant '
          + 'entero. No se puede deshacer. La configuración, las personas y la base de conocimiento '
          + 'quedan intactas.'
        }
        confirmText={corriendo ? 'Borrando…' : 'Borrar todo lo operativo'}
        confirmDisabled={frase.trim() !== FRASE || corriendo}
        onConfirm={correr}
      >
        <div className="space-y-2">
          <p className="text-xs text-zinc-600">
            Escribí <code className="bg-zinc-100 px-1 rounded-sm">{FRASE}</code> para habilitar el
            botón.
          </p>
          <Input
            value={frase}
            onChange={(e) => setFrase(e.target.value)}
            placeholder={FRASE}
            className="rounded-sm text-sm font-mono"
            data-testid="frase-borrado-operativo"
          />
          {corriendo && (
            <p className="text-[11px] text-zinc-500 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Esto borra caso por caso: puede tardar.
            </p>
          )}
        </div>
      </ConfirmActionDialog>
    </Card>
  );
}
